// src/pages/DriverDashboard.js - UPDATED TO USE API SERVICE
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Car,
  MapPin,
  User,
  Bell,
  AlertTriangle,
  Navigation,
  Fuel,
  TrendingUp,
  Shield,
  Battery,
  Thermometer,
  Wrench,
  LogOut,
  Home,
  Map,
  Settings,
  Clock,
  Star,
  Loader,
  PlayCircle,
  StopCircle,
  AlertCircle,
  Navigation2,
  Route,
  Target,
  Coffee,
  Droplets,
  X,
  Maximize2,
  Minimize2,
  TrafficCone,
  Fuel as FuelIcon,
  Utensils,
  Wifi,
  Menu,
  X as CloseIcon,
} from "lucide-react";
import {
  GoogleMap,
  LoadScript,
  Marker,
  DirectionsService,
  DirectionsRenderer,
  InfoWindow,
  TrafficLayer,
} from "@react-google-maps/api";
import { driversAPI, vehiclesAPI, alertsAPI } from "../../services/api";

const DriverDashboard = () => {
  const [driverData, setDriverData] = useState(null);
  const [vehicleData, setVehicleData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ratings, setRatings] = useState([]);
  const [ratingStats, setRatingStats] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  // Navigation States
  const [navigationRoutes, setNavigationRoutes] = useState([]);
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [directions, setDirections] = useState(null);
  const [mapCenter, setMapCenter] = useState({ lat: 6.5244, lng: 3.3792 }); // Lagos center
  const [mapZoom, setMapZoom] = useState(12);
  const [nearbyPlaces, setNearbyPlaces] = useState([]);
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  const [trafficInfo, setTrafficInfo] = useState(null);
  const [placesLoading, setPlacesLoading] = useState(false);

  // Mobile Menu State
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const mapRef = useRef(null);
  const placesService = useRef(null);
  const directionsService = useRef(null);
  const navigate = useNavigate();

  const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  const PRIMARY_COLOR = "#e28743";
  const SECONDARY_COLOR = "#304a3f";

  // Map container style
  const mapContainerStyle = {
    width: "100%",
    height: isMapFullscreen ? "calc(100vh - 200px)" : "400px",
    borderRadius: "8px",
  };

  // Map options
  const mapOptions = {
    disableDefaultUI: false,
    zoomControl: true,
    streetViewControl: true,
    mapTypeControl: true,
    fullscreenControl: false,
    styles: [
      {
        featureType: "poi",
        elementType: "labels",
        stylers: [{ visibility: "on" }],
      },
    ],
  };

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    const token = localStorage.getItem("token");

    if (!storedUser || !token) {
      navigate("/login");
      return;
    }

    const userData = JSON.parse(storedUser);
    if (userData.role !== "driver") {
      navigate("/dashboard");
      return;
    }

    fetchDriverData();
    fetchDriverAlerts();
  }, [navigate]);

  useEffect(() => {
    if (driverData && driverData._id) {
      fetchDriverRatings();
    }
  }, [driverData]);

  useEffect(() => {
    if (
      activeTab === "navigation" &&
      driverData?.currentLocation?.coordinates
    ) {
      const [lng, lat] = driverData.currentLocation.coordinates;
      setMapCenter({ lat, lng });
      setMapZoom(14);
      fetchNearbyPlacesFromGoogle(lat, lng);
      fetchTrafficInfo(lat, lng);
    }
  }, [activeTab, driverData]);

  const fetchDriverData = async () => {
    try {
      setLoading(true);
      
      // Use driversAPI service
      const response = await driversAPI.getDriverProfile();
      
      if (response.success) {
        const driver = response.data;
        setDriverData(driver);

        if (driver.assignedVehicle) {
          await fetchVehicleData(driver.assignedVehicle._id);
        }
      }
    } catch (error) {
      console.error("Error fetching driver data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicleData = async (vehicleId) => {
    try {
      // Use vehiclesAPI service
      const response = await vehiclesAPI.getById(vehicleId);
      
      if (response.success) {
        setVehicleData(response.data);
      }
    } catch (error) {
      console.error("Error fetching vehicle data:", error);
    }
  };

  const fetchDriverAlerts = async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));

      // Use alertsAPI service with parameters
      const response = await alertsAPI.getAll({
        driver: user.driverProfile?._id || user.id,
        limit: 5,
        sort: "-createdAt",
      });
      
      if (response.success) {
        setAlerts(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching alerts:", error);
    }
  };

  const fetchDriverRatings = async () => {
    try {
      // Use driversAPI service
      const response = await driversAPI.getRatings(driverData._id);
      
      if (response.success) {
        setRatings(response.data.ratings || []);
        setRatingStats(response.data.driver);
      }
    } catch (error) {
      console.error("Error fetching ratings:", error);
    }
  };

  const fetchNearbyPlacesFromGoogle = async (lat, lng) => {
    try {
      if (!isGoogleMapsLoaded || !window.google) {
        console.log("Google Maps not loaded yet");
        return;
      }

      setPlacesLoading(true);

      // Create a PlacesService instance
      if (!placesService.current && window.google.maps.places) {
        placesService.current = new window.google.maps.places.PlacesService(
          document.createElement("div")
        );
      }

      // Search for important places for drivers
      const placeTypes = [
        "gas_station",
        "restaurant",
        "car_repair",
        "parking",
        "lodging",
        "cafe",
        "hospital",
        "police",
      ];

      const allPlaces = [];

      // Search for each type of place
      for (const type of placeTypes) {
        const request = {
          location: new window.google.maps.LatLng(lat, lng),
          radius: 5000, // 5km radius
          type: type,
        };

        try {
          const results = await new Promise((resolve, reject) => {
            placesService.current.nearbySearch(request, (results, status) => {
              if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                resolve(results || []);
              } else {
                resolve([]);
              }
            });
          });

          // Add type information to each place
          const typedResults = results.slice(0, 3).map((place) => ({
            id: place.place_id,
            name: place.name,
            address: place.vicinity,
            types: [type],
            location: {
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng(),
            },
            rating: place.rating || null,
            openNow: place.opening_hours?.open_now || null,
          }));

          allPlaces.push(...typedResults);
        } catch (error) {
          console.error(`Error fetching ${type} places:`, error);
        }
      }

      // Remove duplicates and limit results
      const uniquePlaces = Array.from(
        new Map(allPlaces.map((place) => [place.id, place])).values()
      ).slice(0, 10);

      setNearbyPlaces(uniquePlaces);

      // Note: Removed the nearby vehicles API call since it wasn't in the driversAPI service
      // If you need this functionality, add the endpoint to your API service
      
    } catch (error) {
      console.error("Error fetching nearby places:", error);
      setNearbyPlaces([]);
    } finally {
      setPlacesLoading(false);
    }
  };

  const fetchTrafficInfo = (lat, lng) => {
    // This function would integrate with a traffic API
    // For now, we'll simulate traffic data
    setTimeout(() => {
      const trafficConditions = [
        { level: "light", description: "Light traffic", color: "green" },
        { level: "moderate", description: "Moderate traffic", color: "yellow" },
        { level: "heavy", description: "Heavy traffic", color: "orange" },
        { level: "severe", description: "Severe congestion", color: "red" },
      ];

      const randomTraffic =
        trafficConditions[Math.floor(Math.random() * trafficConditions.length)];

      setTrafficInfo({
        ...randomTraffic,
        updateTime: new Date().toLocaleTimeString(),
        location: `Around ${lat.toFixed(4)}, ${lng.toFixed(4)}`,
      });
    }, 1000);
  };

  const updateLocation = async () => {
    try {
      if (!navigator.geolocation) {
        alert("Geolocation is not supported by your browser");
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const locationData = {
            coordinates: [position.coords.longitude, position.coords.latitude],
            address: "Current location",
            accuracy: position.coords.accuracy,
          };

          try {
            // Use driversAPI service
            const response = await driversAPI.updateDriverLocation(locationData);

            if (response.success) {
              // Update map center
              setMapCenter({
                lat: position.coords.latitude,
                lng: position.coords.longitude,
              });
              setMapZoom(15);

              // Refresh nearby places and traffic info
              fetchNearbyPlacesFromGoogle(
                position.coords.latitude,
                position.coords.longitude
              );
              fetchTrafficInfo(
                position.coords.latitude,
                position.coords.longitude
              );

              alert("Location updated successfully!");
              fetchDriverData();
            } else {
              alert(`Failed: ${response.message}`);
            }
          } catch (apiError) {
            alert(
              `API Error: ${
                apiError.response?.data?.message || apiError.message
              }`
            );
          }
        },
        (geolocationError) => {
          alert("Unable to fetch location. Please check browser permissions.");
        }
      );
    } catch (error) {
      alert(`Failed to update location: ${error.message}`);
    }
  };

  const updateFuelLevel = async (level) => {
    try {
      const vehicleId = vehicleData?._id;

      if (!vehicleId) {
        alert("No vehicle assigned");
        return;
      }

      // Use vehiclesAPI service
      await vehiclesAPI.updateFuel(vehicleId, {
        fuelLevel: level,
      });

      fetchVehicleData(vehicleId);
      alert("Fuel level updated successfully!");
    } catch (error) {
      console.error("Error updating fuel level:", error);
      alert(`Failed to update fuel level: ${error.message}`);
    }
  };

  const startTracking = async () => {
    try {
      const vehicleId = driverData?.assignedVehicle?._id;

      // Use driversAPI service
      await driversAPI.startTracking(driverData._id, {
        vehicleId: vehicleId,
        updateInterval: 30,
      });

      // Note: The backend automatically creates an alert now, so this is optional
      try {
        await alertsAPI.create({
          type: "safety",
          severity: "critical",
          title: "DRIVER STARTED TRACKING - ON ROUTE",
          message: `Driver ${
            driverData?.name
          } has started tracking and is now on route. Vehicle: ${
            vehicleData?.registration || "No vehicle"
          }`,
          driver: driverData._id,
          vehicle: vehicleId,
          company: driverData.company?._id,
          data: {
            autoGenerated: true,
            trackingStarted: true,
            emergency: true,
          },
        });
      } catch (alertError) {
        console.log("Alert creation optional:", alertError.message);
      }

      alert("Tracking started!");
      fetchDriverData();
      fetchDriverAlerts(); // Refresh alerts
    } catch (error) {
      console.error("Error starting tracking:", error);
      alert(`Failed to start tracking: ${error.message}`);
    }
  };

  const stopTracking = async () => {
    try {
      // Use driversAPI service
      await driversAPI.stopTracking(driverData._id);

      alert("Tracking stopped!");
      fetchDriverData();
      fetchDriverAlerts(); // Refresh alerts
    } catch (error) {
      console.error("Error stopping tracking:", error);
      alert(`Failed to stop tracking: ${error.message}`);
    }
  };

  const sendEmergencyAlert = async () => {
    try {
      await alertsAPI.create({
        type: "safety",
        severity: "critical",
        title: "EMERGENCY ALERT - SOS",
        message: `Driver ${driverData?.name} has activated emergency alert`,
        driver: driverData._id,
        vehicle: driverData?.assignedVehicle?._id,
        company: driverData.company?._id,
        data: { autoGenerated: true, emergency: true },
      });

      alert("Emergency SOS alert sent to admin!");
      fetchDriverAlerts();
    } catch (error) {
      console.error("Error sending emergency alert:", error);
      alert(`Failed to send emergency alert: ${error.message}`);
    }
  };

  const calculateRoute = (destination) => {
    if (!driverData?.currentLocation?.coordinates || !destination?.location)
      return;

    const [lng, lat] = driverData.currentLocation.coordinates;
    const origin = { lat, lng };

    setSelectedDestination(destination);

    // Use Directions Service
    if (window.google && window.google.maps) {
      const directionsService = new window.google.maps.DirectionsService();

      directionsService.route(
        {
          origin: origin,
          destination: destination.location,
          travelMode: window.google.maps.TravelMode.DRIVING,
          provideRouteAlternatives: true,
          drivingOptions: {
            departureTime: new Date(),
            trafficModel: "bestguess",
          },
        },
        (result, status) => {
          if (status === window.google.maps.DirectionsStatus.OK) {
            setDirections(result);
          } else {
            console.error("Error calculating route:", status);
            alert("Could not calculate route. Please try again.");
          }
        }
      );
    }
  };

  const clearRoute = () => {
    setDirections(null);
    setSelectedDestination(null);
  };

  const openGoogleMapsNavigation = (destination) => {
    if (!driverData?.currentLocation?.coordinates || !destination?.location)
      return;

    const [lng, lat] = driverData.currentLocation.coordinates;
    const origin = `${lat},${lng}`;
    const dest = `${destination.location.lat},${destination.location.lng}`;

    // Open Google Maps in new tab with directions
    window.open(
      `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${dest}&travelmode=driving`,
      "_blank"
    );
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  const onMapLoad = (map) => {
    mapRef.current = map;
    setIsGoogleMapsLoaded(true);
  };

  const getPlaceIcon = (types) => {
    if (types.includes("gas_station")) {
      return { icon: FuelIcon, color: "red", label: "Fuel Station" };
    } else if (types.includes("restaurant") || types.includes("cafe")) {
      return { icon: Utensils, color: "green", label: "Food" };
    } else if (types.includes("car_repair")) {
      return { icon: Wrench, color: "blue", label: "Service" };
    } else if (types.includes("parking")) {
      return { icon: Car, color: "purple", label: "Parking" };
    } else if (types.includes("lodging")) {
      return { icon: Wifi, color: "orange", label: "Lodging" };
    } else if (types.includes("hospital")) {
      return { icon: AlertCircle, color: "pink", label: "Hospital" };
    } else if (types.includes("police")) {
      return { icon: Shield, color: "navy", label: "Police" };
    }
    return { icon: MapPin, color: "gray", label: "Place" };
  };

  // Navigation items
  const navItems = [
    { id: "overview", name: "Overview", icon: Home },
    { id: "navigation", name: "Navigation", icon: Navigation2 },
    { id: "ratings", name: "Ratings & Reviews", icon: Star },
    { id: "alerts", name: "Alerts", icon: Bell },
    { id: "profile", name: "My Profile", icon: Settings },
  ];

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setIsMobileMenuOpen(false); // Close mobile menu when tab is selected
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Loader
            className="w-12 h-12 animate-spin mx-auto mb-4"
            style={{ color: PRIMARY_COLOR }}
          />
          <p className="text-gray-600">Loading driver data...</p>
        </div>
      </div>
    );
  }

  if (!driverData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            Driver Data Not Found
          </h2>
          <p className="text-gray-600 mb-4">
            Unable to load driver information
          </p>
          <button
            onClick={() => navigate("/login")}
            className="px-4 py-2 bg-[#e28743] text-white rounded-lg hover:bg-opacity-90"
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Sidebar Menu */}
      <div className={`
        fixed top-0 right-0 h-full w-64 bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out lg:hidden
        ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}
      `}>
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#e28743] to-[#304a3f] flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="font-medium text-gray-900">
                {driverData?.name || "Driver"}
              </span>
              <p className="text-xs text-gray-500">
                {driverData?.licenseNumber || "License: N/A"}
              </p>
            </div>
          </div>
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <CloseIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-4">
          <div className="space-y-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => handleTabChange(item.id)}
                  className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg font-medium transition-colors ${
                    activeTab === item.id
                      ? "bg-gradient-to-r from-[#e28743] to-[#f0a869] text-white"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {item.name}
                </button>
              );
            })}
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 w-full px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <LogOut className="w-5 h-5" />
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="p-4 md:p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <header className="mb-6 md:mb-8">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#e28743] to-[#304a3f] flex items-center justify-center">
                  <Car className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900">
                    Driver Portal
                  </h1>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="px-2 md:px-3 py-1 bg-[#304a3f] text-white rounded-full text-xs md:text-sm font-medium">
                      {driverData?.status?.toUpperCase() || "ACTIVE"}
                    </span>
                    <span className="text-gray-600 text-xs md:text-sm">
                      ID: {driverData?.driverId || "N/A"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                {/* Mobile Hamburger Menu */}
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="lg:hidden p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50"
                >
                  <Menu className="w-6 h-6 text-gray-600" />
                </button>

                <button
                  onClick={() => setActiveTab("alerts")}
                  className="relative p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 transition-colors"
                >
                  <Bell className="w-5 h-5 text-gray-600" />
                  {alerts.length > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {alerts.length}
                    </span>
                  )}
                </button>

                <div className="hidden lg:flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#e28743] to-[#304a3f] flex items-center justify-center">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <span className="font-medium text-gray-900">
                      {driverData?.name || "Driver"}
                    </span>
                    <p className="text-xs text-gray-500">
                      {driverData?.licenseNumber || "License: N/A"}
                    </p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Logout"
                  >
                    <LogOut className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
              </div>
            </div>

            {/* Desktop Navigation Tabs - Hidden on mobile */}
            <nav className="hidden lg:flex flex-wrap gap-2 md:gap-4 mt-6">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                      activeTab === item.id
                        ? "bg-gradient-to-r from-[#e28743] to-[#f0a869] text-white"
                        : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-200"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {item.name}
                  </button>
                );
              })}
            </nav>
          </header>

          {/* Main Content */}
          {activeTab === "overview" && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - 2/3 width */}
              <div className="lg:col-span-2 space-y-6">
                {/* Driver Stats Card */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
                    <h2 className="text-lg font-semibold text-gray-900">
                      Driver Statistics
                    </h2>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-500">Status:</span>
                      <span
                        className={`px-3 py-1 rounded-full font-medium text-sm ${
                          driverData?.status === "on_duty" ||
                          driverData?.status === "on_trip"
                            ? "bg-green-100 text-green-800"
                            : driverData?.status === "active"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {driverData?.status?.toUpperCase() || "INACTIVE"}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                    {/* Trips */}
                    <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl">
                      <div className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-3 rounded-full bg-blue-100 flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 md:w-6 md:h-6 text-blue-600" />
                      </div>
                      <h3 className="text-2xl md:text-3xl font-bold text-gray-900">
                        {driverData?.totalTrips || 0}
                      </h3>
                      <p className="text-gray-600">Total Trips</p>
                      <p className="text-xs md:text-sm text-gray-500 mt-1">
                        {driverData?.totalHours || 0} hours driven
                      </p>
                    </div>

                    {/* Experience */}
                    <div className="text-center p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-xl">
                      <div className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-3 rounded-full bg-green-100 flex items-center justify-center">
                        <Clock className="w-5 h-5 md:w-6 md:h-6 text-green-600" />
                      </div>
                      <h3 className="text-2xl md:text-3xl font-bold text-gray-900">
                        {driverData?.experience || 0}
                      </h3>
                      <p className="text-gray-600">Years Experience</p>
                      <p className="text-xs md:text-sm text-gray-500 mt-1 capitalize">
                        {driverData?.driverType || "Driver"}
                      </p>
                    </div>

                    {/* Current Vehicle */}
                    <div className="text-center p-4 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl">
                      <div className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-3 rounded-full bg-orange-100 flex items-center justify-center">
                        <Car className="w-5 h-5 md:w-6 md:h-6 text-orange-600" />
                      </div>
                      <h3 className="text-base md:text-lg font-bold text-gray-900 truncate">
                        {vehicleData?.registration || "N/A"}
                      </h3>
                      <p className="text-gray-600">Assigned Vehicle</p>
                      <p className="text-xs md:text-sm text-gray-500 mt-1">
                        {vehicleData?.model || "No vehicle"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Vehicle Status */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-6">
                    Vehicle Status
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Fuel Level */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="font-medium text-gray-700">Fuel Level</h3>
                        <Fuel className="w-5 h-5 text-[#304a3f]" />
                      </div>

                      {vehicleData?.fuelLevel !== undefined ? (
                        <>
                          <div className="relative">
                            <div className="h-6 bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full ${
                                  vehicleData.fuelLevel < 20
                                    ? "bg-gradient-to-r from-red-500 to-orange-500"
                                    : vehicleData.fuelLevel < 50
                                    ? "bg-gradient-to-r from-yellow-500 to-orange-400"
                                    : "bg-gradient-to-r from-green-500 to-green-600"
                                }`}
                                style={{ width: `${vehicleData.fuelLevel}%` }}
                              />
                            </div>
                            <div className="flex justify-between mt-2">
                              <span className="text-xs md:text-sm text-gray-500">Empty</span>
                              <span
                                className={`text-xs md:text-sm font-semibold ${
                                  vehicleData.fuelLevel < 20
                                    ? "text-red-600"
                                    : vehicleData.fuelLevel < 50
                                    ? "text-orange-600"
                                    : "text-green-600"
                                }`}
                              >
                                {vehicleData.fuelLevel}%
                                {vehicleData.fuelLevel < 20 ? " - Low Fuel!" : ""}
                              </span>
                              <span className="text-xs md:text-sm text-gray-500">Full</span>
                            </div>
                          </div>

                          <div className="grid grid-cols-3 gap-2">
                            <button
                              onClick={() => updateFuelLevel(25)}
                              className="py-2 bg-red-100 text-red-800 rounded-lg text-xs md:text-sm hover:bg-red-200"
                            >
                              Low (25%)
                            </button>
                            <button
                              onClick={() => updateFuelLevel(50)}
                              className="py-2 bg-yellow-100 text-yellow-800 rounded-lg text-xs md:text-sm hover:bg-yellow-200"
                            >
                              Half (50%)
                            </button>
                            <button
                              onClick={() => updateFuelLevel(85)}
                              className="py-2 bg-green-100 text-green-800 rounded-lg text-xs md:text-sm hover:bg-green-200"
                            >
                              Full (85%)
                            </button>
                          </div>
                        </>
                      ) : (
                        <div className="text-center py-4 bg-gray-50 rounded-lg">
                          <p className="text-gray-500">
                            No vehicle data available
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Location & Mileage */}
                    <div className="space-y-4">
                      <div>
                        <h3 className="font-medium text-gray-700 mb-2">
                          Current Location
                        </h3>
                        {driverData?.currentLocation?.address ? (
                          <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                            <MapPin className="w-5 h-5 text-[#e28743] flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="font-medium text-sm md:text-base">
                                {driverData.currentLocation.address}
                              </p>
                              <p className="text-xs md:text-sm text-gray-600 mt-1">
                                Lat:{" "}
                                {driverData.currentLocation.coordinates[1]?.toFixed(
                                  4
                                )}
                                , Lng:{" "}
                                {driverData.currentLocation.coordinates[0]?.toFixed(
                                  4
                                )}
                              </p>
                            </div>
                          </div>
                        ) : (
                          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <p className="text-yellow-800">Location not set</p>
                            <button
                              onClick={updateLocation}
                              className="mt-2 text-sm text-[#e28743] hover:underline"
                            >
                              Set location now
                            </button>
                          </div>
                        )}
                      </div>

                      {vehicleData?.mileage && (
                        <div className="p-4 bg-gradient-to-r from-[#304a3f]/10 to-[#e28743]/10 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-xs md:text-sm text-gray-600">
                                Total Mileage
                              </p>
                              <p className="text-xl md:text-2xl font-bold text-[#304a3f]">
                                {vehicleData.mileage.toLocaleString()} km
                              </p>
                            </div>
                            <TrendingUp className="w-6 h-6 md:w-8 md:h-8 text-[#e28743]" />
                          </div>
                          {vehicleData.lastService && (
                            <p className="text-xs text-gray-500 mt-2">
                              Last service:{" "}
                              {new Date(
                                vehicleData.lastService
                              ).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-6">
                    Quick Actions
                  </h2>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                    <button
                      onClick={startTracking}
                      className="flex flex-col items-center justify-center p-3 md:p-4 bg-gradient-to-r from-[#e28743] to-[#f0a869] rounded-lg text-white hover:shadow-md transition-shadow"
                    >
                      <PlayCircle className="w-6 h-6 md:w-8 md:h-8 mb-1 md:mb-2" />
                      <span className="font-medium text-xs md:text-sm">Start Tracking</span>
                    </button>

                    <button
                      onClick={stopTracking}
                      className="flex flex-col items-center justify-center p-3 md:p-4 bg-white border-2 border-[#304a3f] rounded-lg text-[#304a3f] hover:bg-[#304a3f]/5 transition-colors"
                    >
                      <StopCircle className="w-6 h-6 md:w-8 md:h-8 mb-1 md:mb-2" />
                      <span className="font-medium text-xs md:text-sm">Stop Tracking</span>
                    </button>

                    <button
                      onClick={updateLocation}
                      className="flex flex-col items-center justify-center p-3 md:p-4 bg-white border-2 border-blue-500 rounded-lg text-blue-500 hover:bg-blue-50 transition-colors"
                    >
                      <MapPin className="w-6 h-6 md:w-8 md:h-8 mb-1 md:mb-2" />
                      <span className="font-medium text-xs md:text-sm">Update Location</span>
                    </button>

                    <button
                      onClick={sendEmergencyAlert}
                      className="flex flex-col items-center justify-center p-3 md:p-4 bg-white border-2 border-red-500 rounded-lg text-red-500 hover:bg-red-50 transition-colors"
                    >
                      <AlertCircle className="w-6 h-6 md:w-8 md:h-8 mb-1 md:mb-2" />
                      <span className="font-medium text-xs md:text-sm">Emergency SOS</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Right Column - 1/3 width */}
              <div className="space-y-6">
                {/* Driver Rating */}
                <div className="bg-gradient-to-br from-[#304a3f] to-[#1a2a23] rounded-xl shadow-sm p-4 md:p-6 text-white">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-semibold">Driver Rating</h2>
                    <Star className="w-6 h-6 text-yellow-400" />
                  </div>

                  <div className="text-center mb-6">
                    <div className="text-4xl md:text-5xl font-bold mb-2 text-yellow-400">
                      {ratingStats?.averageRating?.toFixed(1) ||
                        driverData?.rating?.toFixed(1) ||
                        "0.0"}
                    </div>
                    <div className="flex justify-center gap-1 mb-2">
                      {[...Array(5)].map((_, i) => (
                        <span
                          key={i}
                          className={`text-xl md:text-2xl ${
                            i <
                            Math.floor(
                              ratingStats?.averageRating ||
                                driverData?.rating ||
                                0
                            )
                              ? "text-yellow-400"
                              : "text-gray-400"
                          }`}
                        >
                          ★
                        </span>
                      ))}
                    </div>
                    <p className="text-gray-300">Overall Rating</p>
                    <p className="text-xs md:text-sm text-gray-400 mt-1">
                      Based on {ratingStats?.totalRatings || 0} reviews
                    </p>
                  </div>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-300">Total Trips</span>
                      <span className="font-semibold">
                        {driverData?.totalTrips || 0}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-300">Years Experience</span>
                      <span className="font-semibold">
                        {driverData?.experience || 0}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-300">Status</span>
                      <span
                        className={`px-2 py-1 rounded text-xs ${
                          driverData?.status === "active"
                            ? "bg-green-500"
                            : driverData?.status === "on_duty"
                            ? "bg-blue-500"
                            : driverData?.status === "on_trip"
                            ? "bg-purple-500"
                            : "bg-gray-500"
                        }`}
                      >
                        {driverData?.status?.toUpperCase()}
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={() => setActiveTab("ratings")}
                    className="w-full py-3 bg-gradient-to-r from-[#e28743] to-[#f0a869] rounded-lg font-medium hover:shadow-md transition-shadow text-sm md:text-base"
                  >
                    View All Reviews
                  </button>
                </div>

                {/* Active Alerts */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-semibold text-gray-900">
                      Active Alerts
                    </h2>
                    {alerts.filter((a) => a.severity === "critical").length >
                      0 && (
                      <span className="px-2 md:px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs md:text-sm font-medium">
                        {alerts.filter((a) => a.severity === "critical").length}{" "}
                        critical
                      </span>
                    )}
                  </div>

                  {alerts.length > 0 ? (
                    <div className="space-y-4">
                      {alerts.slice(0, 3).map((alert, index) => (
                        <div
                          key={alert._id || index}
                          className={`p-3 rounded-lg border ${
                            alert.severity === "critical"
                              ? "border-red-200 bg-red-50"
                              : alert.severity === "high"
                              ? "border-orange-200 bg-orange-50"
                              : alert.severity === "medium"
                              ? "border-yellow-200 bg-yellow-50"
                              : "border-blue-200 bg-blue-50"
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            <AlertTriangle
                              className={`w-4 h-4 md:w-5 md:h-5 mt-0.5 ${
                                alert.severity === "critical"
                                  ? "text-red-600"
                                  : alert.severity === "high"
                                  ? "text-orange-600"
                                  : alert.severity === "medium"
                                  ? "text-yellow-600"
                                  : "text-blue-600"
                              }`}
                            />
                            <div className="flex-1">
                              <h4 className="font-medium text-gray-900 mb-1 text-xs md:text-sm">
                                {alert.title}
                              </h4>
                              <p className="text-xs text-gray-600">
                                {alert.message}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                {new Date(alert.createdAt).toLocaleString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 md:py-8">
                      <div className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                        <Shield className="w-5 h-5 md:w-6 md:h-6 text-green-600" />
                      </div>
                      <p className="text-gray-600">No active alerts</p>
                      <p className="text-xs md:text-sm text-gray-500 mt-1">
                        All systems are normal
                      </p>
                    </div>
                  )}

                  <button
                    onClick={() => setActiveTab("alerts")}
                    className="w-full mt-6 py-3 border-2 border-[#304a3f] text-[#304a3f] font-medium rounded-lg hover:bg-[#304a3f]/5 transition-colors text-sm md:text-base"
                  >
                    View All Alerts
                  </button>
                </div>

                {/* Vehicle Info */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-6">
                    Vehicle Information
                  </h2>

                  {vehicleData ? (
                    <div className="space-y-4">
                      <div className="p-4 bg-gradient-to-r from-[#304a3f]/5 to-[#304a3f]/10 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-mono text-base md:text-lg font-bold text-[#304a3f]">
                            {vehicleData.registration}
                          </span>
                          <Car className="w-5 h-5 text-[#304a3f]" />
                        </div>
                        <p className="text-gray-700 text-sm md:text-base">
                          {vehicleData.make || ""} {vehicleData.model || ""}
                          {vehicleData.year ? ` (${vehicleData.year})` : ""}
                        </p>
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <div className="flex items-center justify-between">
                            <span className="text-xs md:text-sm text-gray-600">Status</span>
                            <span
                              className={`text-xs md:text-sm font-medium px-2 py-1 rounded ${
                                vehicleData.status === "available"
                                  ? "bg-green-100 text-green-800"
                                  : vehicleData.status === "on_trip"
                                  ? "bg-blue-100 text-blue-800"
                                  : vehicleData.status === "assigned"
                                  ? "bg-purple-100 text-purple-800"
                                  : vehicleData.status === "maintenance"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {vehicleData.status?.toUpperCase()}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-100 rounded-lg">
                              <Battery className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-700 text-sm md:text-base">
                                Next Service
                              </p>
                              <p className="text-xs md:text-sm text-gray-500">Due date</p>
                            </div>
                          </div>
                          <span className="font-medium text-sm md:text-base">
                            {vehicleData.nextService
                              ? new Date(
                                  vehicleData.nextService
                                ).toLocaleDateString()
                              : "N/A"}
                          </span>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-green-100 rounded-lg">
                              <Wrench className="w-4 h-4 md:w-5 md:h-5 text-green-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-700 text-sm md:text-base">
                                Last Service
                              </p>
                              <p className="text-xs md:text-sm text-gray-500">Date</p>
                            </div>
                          </div>
                          <span className="font-medium text-sm md:text-base">
                            {vehicleData.lastService
                              ? new Date(
                                  vehicleData.lastService
                                ).toLocaleDateString()
                              : "N/A"}
                          </span>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-purple-100 rounded-lg">
                              <Thermometer className="w-4 h-4 md:w-5 md:h-5 text-purple-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-700 text-sm md:text-base">
                                Vehicle Type
                              </p>
                              <p className="text-xs md:text-sm text-gray-500">Category</p>
                            </div>
                          </div>
                          <span className="font-medium text-sm md:text-base capitalize">
                            {vehicleData.vehicleType || "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-6 md:py-8">
                      <Car className="w-10 h-10 md:w-12 md:h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600">No vehicle assigned</p>
                      <p className="text-xs md:text-sm text-gray-500 mt-1">
                        Contact your company admin
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Navigation Tab */}
          {activeTab === "navigation" && (
            <div className="space-y-6">
              {/* Map Controls */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      Real-time Navigation
                    </h2>
                    <p className="text-sm text-gray-500">
                      Live Google Maps with traffic data and nearby places
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={updateLocation}
                      className="flex items-center gap-2 px-3 md:px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm md:text-base"
                    >
                      <MapPin className="w-4 h-4" />
                      Update My Location
                    </button>
                    <button
                      onClick={() => setIsMapFullscreen(!isMapFullscreen)}
                      className="flex items-center gap-2 px-3 md:px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm md:text-base"
                    >
                      {isMapFullscreen ? (
                        <>
                          <Minimize2 className="w-4 h-4" />
                          <span className="hidden md:inline">Exit Fullscreen</span>
                        </>
                      ) : (
                        <>
                          <Maximize2 className="w-4 h-4" />
                          <span className="hidden md:inline">Fullscreen</span>
                        </>
                      )}
                    </button>
                    {directions && (
                      <button
                        onClick={clearRoute}
                        className="flex items-center gap-2 px-3 md:px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-sm md:text-base"
                      >
                        <X className="w-4 h-4" />
                        <span className="hidden md:inline">Clear Route</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Traffic Info */}
                {trafficInfo && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div className="flex items-center gap-3">
                        <TrafficCone
                          className={`w-5 h-5 ${
                            trafficInfo.color === "green"
                              ? "text-green-600"
                              : trafficInfo.color === "yellow"
                              ? "text-yellow-600"
                              : trafficInfo.color === "orange"
                              ? "text-orange-600"
                              : "text-red-600"
                          }`}
                        />
                        <div>
                          <span className="font-medium text-gray-900 text-sm md:text-base">
                            Traffic: {trafficInfo.description}
                          </span>
                          <p className="text-xs md:text-sm text-gray-500">
                            Updated: {trafficInfo.updateTime}
                          </p>
                        </div>
                      </div>
                      <span
                        className={`px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-medium ${
                          trafficInfo.color === "green"
                            ? "bg-green-100 text-green-800"
                            : trafficInfo.color === "yellow"
                            ? "bg-yellow-100 text-yellow-800"
                            : trafficInfo.color === "orange"
                            ? "bg-orange-100 text-orange-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {trafficInfo.level.toUpperCase()}
                      </span>
                    </div>
                  </div>
                )}

                {/* Google Maps Container */}
                <div className="relative rounded-lg overflow-hidden border border-gray-200">
                  <LoadScript
                    googleMapsApiKey={GOOGLE_MAPS_API_KEY}
                    libraries={["places"]}
                    loadingElement={
                      <div className="h-[300px] md:h-[400px] w-full bg-gray-100 animate-pulse" />
                    }
                  >
                    <GoogleMap
                      mapContainerStyle={{
                        ...mapContainerStyle,
                        height: isMapFullscreen ? "calc(100vh - 200px)" : "300px",
                      }}
                      center={mapCenter}
                      zoom={mapZoom}
                      options={mapOptions}
                      onLoad={onMapLoad}
                    >
                      {/* Traffic Layer */}
                      <TrafficLayer />

                      {/* Driver Marker */}
                      {isGoogleMapsLoaded &&
                        driverData?.currentLocation?.coordinates && (
                          <Marker
                            position={{
                              lat: driverData.currentLocation.coordinates[1],
                              lng: driverData.currentLocation.coordinates[0],
                            }}
                            icon={{
                              path: window.google?.maps?.SymbolPath?.CIRCLE,
                              fillColor: PRIMARY_COLOR,
                              fillOpacity: 1,
                              strokeColor: "#FFFFFF",
                              strokeWeight: 2,
                              scale: 10,
                            }}
                            title="Your Location"
                          />
                        )}

                      {/* Nearby Places Markers */}
                      {isGoogleMapsLoaded &&
                        nearbyPlaces.map((place) => (
                          <Marker
                            key={place.id}
                            position={place.location}
                            onClick={() => setSelectedPlace(place)}
                            icon={{
                              url: `data:image/svg+xml,${encodeURIComponent(`
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                                  <circle cx="12" cy="12" r="10" fill="${
                                    getPlaceIcon(place.types).color
                                  }" opacity="0.8"/>
                                  <circle cx="12" cy="12" r="8" fill="white"/>
                                  <text x="12" y="16" text-anchor="middle" font-size="10" fill="${
                                    getPlaceIcon(place.types).color
                                  }" font-weight="bold">
                                    ${getPlaceIcon(place.types).label.charAt(0)}
                                  </text>
                                </svg>
                              `)}`,
                              scaledSize: new window.google.maps.Size(30, 30),
                            }}
                            title={place.name}
                          />
                        ))}

                      {/* Selected Place InfoWindow */}
                      {selectedPlace && (
                        <InfoWindow
                          position={selectedPlace.location}
                          onCloseClick={() => setSelectedPlace(null)}
                        >
                          <div className="p-2 min-w-[200px]">
                            <div className="flex items-center gap-2 mb-2">
                              <div
                                className={`w-8 h-8 rounded-full bg-${
                                  getPlaceIcon(selectedPlace.types).color
                                }-100 flex items-center justify-center`}
                              >
                                {(() => {
                                  const Icon = getPlaceIcon(selectedPlace.types)
                                    .icon;
                                  return (
                                    <Icon
                                      className={`w-4 h-4 text-${
                                        getPlaceIcon(selectedPlace.types).color
                                      }-600`}
                                    />
                                  );
                                })()}
                              </div>
                              <div>
                                <h3 className="font-bold text-sm">
                                  {selectedPlace.name}
                                </h3>
                                <p className="text-xs text-gray-600">
                                  {getPlaceIcon(selectedPlace.types).label}
                                </p>
                              </div>
                            </div>
                            <p className="text-xs text-gray-600 mb-2">
                              {selectedPlace.address}
                            </p>
                            <div className="flex items-center justify-between text-xs mb-2">
                              {selectedPlace.rating && (
                                <span className="flex items-center">
                                  <Star className="w-3 h-3 text-yellow-400 mr-1" />
                                  {selectedPlace.rating}/5
                                </span>
                              )}
                              {selectedPlace.openNow !== null && (
                                <span
                                  className={`${
                                    selectedPlace.openNow
                                      ? "text-green-600"
                                      : "text-red-600"
                                  }`}
                                >
                                  {selectedPlace.openNow
                                    ? "Open Now"
                                    : "Closed"}
                                </span>
                              )}
                            </div>
                            <div className="flex gap-2 mt-2">
                              <button
                                onClick={() => {
                                  calculateRoute(selectedPlace);
                                  setSelectedPlace(null);
                                }}
                                className="flex-1 py-1 text-xs bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
                              >
                                Get Directions
                              </button>
                              <button
                                onClick={() =>
                                  openGoogleMapsNavigation(selectedPlace)
                                }
                                className="flex-1 py-1 text-xs bg-green-50 text-green-600 rounded hover:bg-green-100"
                              >
                                Open in Maps
                              </button>
                            </div>
                          </div>
                        </InfoWindow>
                      )}

                      {/* Directions Renderer */}
                      {directions && (
                        <DirectionsRenderer
                          directions={directions}
                          options={{
                            suppressMarkers: true,
                            polylineOptions: {
                              strokeColor: PRIMARY_COLOR,
                              strokeWeight: 5,
                              strokeOpacity: 0.8,
                            },
                          }}
                        />
                      )}

                      {/* Destination Marker */}
                      {isGoogleMapsLoaded && selectedDestination && (
                        <Marker
                          position={selectedDestination.location}
                          icon={{
                            url: `data:image/svg+xml,${encodeURIComponent(`
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="red"/>
                              </svg>
                            `)}`,
                            scaledSize: new window.google.maps.Size(40, 40),
                          }}
                          title={selectedDestination.name}
                        />
                      )}
                    </GoogleMap>
                  </LoadScript>

                  {placesLoading && (
                    <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 md:px-4 py-2 rounded-lg shadow-sm">
                      <div className="flex items-center gap-2">
                        <Loader className="w-4 h-4 animate-spin text-blue-600" />
                        <span className="text-xs md:text-sm text-gray-700">
                          Loading nearby places...
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {selectedDestination && directions && (
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                    <div className="flex flex-col md:flex-row md:items-center justify-between mb-2 gap-2">
                      <h3 className="font-semibold text-gray-900 text-sm md:text-base">
                        Route to {selectedDestination.name}
                      </h3>
                      <button
                        onClick={() =>
                          openGoogleMapsNavigation(selectedDestination)
                        }
                        className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded text-xs md:text-sm hover:bg-blue-200"
                      >
                        <Navigation2 className="w-4 h-4" />
                        <span className="hidden md:inline">Open in Google Maps</span>
                        <span className="md:hidden">Google Maps</span>
                      </button>
                    </div>
                    {directions.routes[0]?.legs[0] && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 mt-3">
                        <div className="text-center p-2 bg-white rounded">
                          <p className="text-xs md:text-sm text-gray-600">Distance</p>
                          <p className="font-semibold text-gray-900 text-sm md:text-base">
                            {directions.routes[0].legs[0].distance.text}
                          </p>
                        </div>
                        <div className="text-center p-2 bg-white rounded">
                          <p className="text-xs md:text-sm text-gray-600">Duration</p>
                          <p className="font-semibold text-gray-900 text-sm md:text-base">
                            {directions.routes[0].legs[0].duration.text}
                          </p>
                        </div>
                        <div className="text-center p-2 bg-white rounded">
                          <p className="text-xs md:text-sm text-gray-600">Traffic</p>
                          <p className="font-semibold text-gray-900 text-sm md:text-base">
                            {directions.routes[0].legs[0].duration_in_traffic
                              ? directions.routes[0].legs[0].duration_in_traffic
                                  .text
                              : "No traffic data"}
                          </p>
                        </div>
                        <div className="text-center p-2 bg-white rounded">
                          <p className="text-xs md:text-sm text-gray-600">Route #</p>
                          <p className="font-semibold text-gray-900 text-sm md:text-base">
                            {directions.routes.length} available
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Nearby Places */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      Nearby Places for Drivers
                    </h2>
                    <p className="text-sm text-gray-500">
                      Fuel stations, restaurants, services, and more
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      if (driverData?.currentLocation?.coordinates) {
                        const [lng, lat] = driverData.currentLocation.coordinates;
                        fetchNearbyPlacesFromGoogle(lat, lng);
                      }
                    }}
                    className="flex items-center gap-2 px-3 md:px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm md:text-base"
                    disabled={placesLoading}
                  >
                    <Loader
                      className={`w-4 h-4 ${placesLoading ? "animate-spin" : "hidden"}`}
                    />
                    Refresh Places
                  </button>
                </div>

                {placesLoading ? (
                  <div className="text-center py-8 md:py-12">
                    <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
                    <p className="text-gray-600">Loading nearby places...</p>
                  </div>
                ) : nearbyPlaces.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {nearbyPlaces.map((place) => {
                      const placeIcon = getPlaceIcon(place.types);
                      const Icon = placeIcon.icon;
                      return (
                        <div
                          key={place.id}
                          className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 transition-colors cursor-pointer group"
                          onClick={() => setSelectedPlace(place)}
                        >
                          <div className="flex items-center gap-3 mb-3">
                            <div
                              className={`w-10 h-10 md:w-12 md:h-12 rounded-full ${`bg-${placeIcon.color}-100`} flex items-center justify-center group-hover:scale-110 transition-transform`}
                            >
                              <Icon
                                className={`w-5 h-5 md:w-6 md:h-6 ${`text-${placeIcon.color}-600`}`}
                              />
                            </div>
                            <div className="flex-1">
                              <h3 className="font-medium text-gray-900 text-sm md:text-base">
                                {place.name}
                              </h3>
                              <div className="flex items-center gap-2 mt-1">
                                <span
                                  className={`text-xs px-2 py-0.5 rounded ${`bg-${placeIcon.color}-100 ${`text-${placeIcon.color}-800`}`}`}
                                >
                                  {placeIcon.label}
                                </span>
                                {place.rating && (
                                  <span className="flex items-center text-xs text-gray-600">
                                    <Star className="w-3 h-3 text-yellow-400 mr-1" />
                                    {place.rating}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          <p className="text-xs text-gray-600 mb-3 line-clamp-2">
                            {place.address}
                          </p>

                          <div className="flex items-center justify-between">
                            {place.openNow !== null && (
                              <span
                                className={`text-xs font-medium ${
                                  place.openNow
                                    ? "text-green-600"
                                    : "text-red-600"
                                }`}
                              >
                                {place.openNow ? "● Open Now" : "● Closed"}
                              </span>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                calculateRoute(place);
                              }}
                              className="text-xs px-3 py-1.5 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors"
                            >
                              Directions
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 md:py-12">
                    <Map className="w-12 h-12 md:w-16 md:h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">
                      No Nearby Places Found
                    </h3>
                    <p className="text-gray-500 mb-6">
                      Update your location to see nearby places
                    </p>
                    <button
                      onClick={updateLocation}
                      className="px-4 md:px-6 py-3 bg-gradient-to-r from-[#e28743] to-[#f0a869] text-white rounded-lg font-medium hover:shadow-md transition-shadow text-sm md:text-base"
                    >
                      Update My Location
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Ratings Tab */}
          {activeTab === "ratings" && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Ratings & Reviews
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    See what people say about your driving
                  </p>
                </div>
                <div>
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <div className="text-3xl md:text-4xl font-bold text-gray-900">
                        {ratingStats?.averageRating?.toFixed(1) || "0.0"}
                      </div>
                      <div className="flex justify-center mt-1">
                        {[...Array(5)].map((_, i) => (
                          <span
                            key={i}
                            className={`text-base md:text-lg ${
                              i < Math.floor(ratingStats?.averageRating || 0)
                                ? "text-yellow-400"
                                : "text-gray-300"
                            }`}
                          >
                            ★
                          </span>
                        ))}
                      </div>
                      <p className="text-xs md:text-sm text-gray-500 mt-1">
                        {ratingStats?.totalRatings || 0} total reviews
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {ratings.length > 0 ? (
                <div className="space-y-6">
                  {ratings.map((rating, index) => (
                    <div
                      key={rating._id || index}
                      className="p-4 border border-gray-200 rounded-lg"
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between mb-3 gap-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#e28743] to-[#304a3f] flex items-center justify-center">
                            <User className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900">
                              {rating.reviewedBy?.name || "Anonymous"}
                            </h4>
                            <p className="text-sm text-gray-500">
                              {new Date(rating.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <span className="text-xl md:text-2xl font-bold text-gray-900 mr-2">
                            {rating.rating.toFixed(1)}
                          </span>
                          <div className="flex">
                            {[...Array(5)].map((_, i) => (
                              <span
                                key={i}
                                className={`${
                                  i < Math.floor(rating.rating)
                                    ? "text-yellow-400"
                                    : "text-gray-300"
                                }`}
                              >
                                ★
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>

                      {rating.feedback && (
                        <p className="text-gray-700 bg-gray-50 p-3 rounded-lg text-sm md:text-base">
                          "{rating.feedback}"
                        </p>
                      )}

                      {rating.categories && (
                        <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-2">
                          {Object.entries(rating.categories).map(
                            ([category, score]) => (
                              <div key={category} className="text-center">
                                <div className="text-xs md:text-sm font-medium text-gray-700 capitalize">
                                  {category.replace("_", " ")}
                                </div>
                                <div className="text-base md:text-lg font-bold text-gray-900">
                                  {score.toFixed(1)}
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 md:py-12">
                  <Star className="w-12 h-12 md:w-16 md:h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">
                    No Ratings Yet
                  </h3>
                  <p className="text-gray-500">
                    You haven't received any ratings yet.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Alerts Tab */}
          {activeTab === "alerts" && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">
                  All Alerts
                </h2>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-500">Total:</span>
                  <span className="px-2 md:px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-xs md:text-sm font-medium">
                    {alerts.length} alerts
                  </span>
                </div>
              </div>

              {alerts.length > 0 ? (
                <div className="space-y-4">
                  {alerts.map((alert, index) => (
                    <div
                      key={alert._id || index}
                      className={`p-4 rounded-lg border ${
                        alert.severity === "critical"
                          ? "border-red-200 bg-red-50"
                          : alert.severity === "high"
                          ? "border-orange-200 bg-orange-50"
                          : alert.severity === "medium"
                          ? "border-yellow-200 bg-yellow-50"
                          : "border-blue-200 bg-blue-50"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={`p-2 rounded-lg ${
                            alert.severity === "critical"
                              ? "bg-red-100"
                              : alert.severity === "high"
                              ? "bg-orange-100"
                              : alert.severity === "medium"
                              ? "bg-yellow-100"
                              : "bg-blue-100"
                          }`}
                        >
                          <AlertTriangle
                            className={`w-5 h-5 ${
                              alert.severity === "critical"
                                ? "text-red-600"
                                : alert.severity === "high"
                                ? "text-orange-600"
                                : alert.severity === "medium"
                                ? "text-yellow-600"
                                : "text-blue-600"
                            }`}
                          />
                        </div>
                        <div className="flex-1">
                          <div className="flex flex-col md:flex-row md:items-start justify-between gap-2">
                            <div>
                              <h4 className="font-medium text-gray-900 mb-1 text-sm md:text-base">
                                {alert.title}
                              </h4>
                              <p className="text-sm text-gray-600">
                                {alert.message}
                              </p>
                            </div>
                            <span
                              className={`px-2 py-1 rounded text-xs font-medium self-start md:self-auto ${
                                alert.severity === "critical"
                                  ? "bg-red-100 text-red-800"
                                  : alert.severity === "high"
                                  ? "bg-orange-100 text-orange-800"
                                  : alert.severity === "medium"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : "bg-blue-100 text-blue-800"
                              }`}
                            >
                              {alert.severity?.toUpperCase()}
                            </span>
                          </div>
                          <div className="flex flex-col md:flex-row md:items-center justify-between mt-3 gap-1">
                            <span className="text-xs text-gray-500">
                              {new Date(alert.createdAt).toLocaleString()}
                            </span>
                            <span className="text-xs text-gray-500">
                              Status:{" "}
                              <span
                                className={`font-medium ${
                                  alert.status === "active"
                                    ? "text-red-600"
                                    : alert.status === "acknowledged"
                                    ? "text-yellow-600"
                                    : "text-green-600"
                                }`}
                              >
                                {alert.status?.toUpperCase()}
                              </span>
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 md:py-12">
                  <Shield className="w-12 h-12 md:w-16 md:h-16 text-green-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">
                    No Active Alerts
                  </h3>
                  <p className="text-gray-500">You're all caught up!</p>
                </div>
              )}

              <div className="mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={sendEmergencyAlert}
                  className="w-full py-3 bg-gradient-to-r from-red-500 to-red-600 text-white font-medium rounded-lg hover:shadow-md transition-shadow text-sm md:text-base"
                >
                  Send Emergency SOS Alert
                </button>
                <p className="text-xs text-gray-500 text-center mt-2">
                  This will send an immediate emergency alert to your company
                  admin
                </p>
              </div>
            </div>
          )}

          {/* Profile Tab */}
          {activeTab === "profile" && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">
                My Profile
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                  {/* Personal Info */}
                  <div className="p-4 md:p-6 border border-gray-200 rounded-lg">
                    <h3 className="font-semibold text-gray-800 mb-4">
                      Personal Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-500 mb-1">
                          Full Name
                        </label>
                        <p className="font-medium text-gray-900">
                          {driverData?.name || "N/A"}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-500 mb-1">
                          Email
                        </label>
                        <p className="font-medium text-gray-900">
                          {driverData?.email || "N/A"}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-500 mb-1">
                          Phone
                        </label>
                        <p className="font-medium text-gray-900">
                          {driverData?.phone || "N/A"}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-500 mb-1">
                          License Number
                        </label>
                        <p className="font-medium text-gray-900">
                          {driverData?.licenseNumber || "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Driving Info */}
                  <div className="p-4 md:p-6 border border-gray-200 rounded-lg">
                    <h3 className="font-semibold text-gray-800 mb-4">
                      Driving Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm text-gray-500 mb-1">
                          Driver Type
                        </label>
                        <p className="font-medium text-gray-900 capitalize">
                          {driverData?.driverType || "N/A"}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-500 mb-1">
                          Years Experience
                        </label>
                        <p className="font-medium text-gray-900">
                          {driverData?.experience || 0} years
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-500 mb-1">
                          Total Trips
                        </label>
                        <p className="font-medium text-gray-900">
                          {driverData?.totalTrips || 0}
                        </p>
                      </div>
                    </div>
                    {driverData?.certifications &&
                      driverData.certifications.length > 0 && (
                        <div className="mt-4">
                          <label className="block text-sm text-gray-500 mb-1">
                            Certifications
                          </label>
                          <div className="flex flex-wrap gap-2">
                            {driverData.certifications.map((cert, index) => (
                              <span
                                key={index}
                                className="px-2 md:px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs md:text-sm"
                              >
                                {cert}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                  </div>

                  {/* Current Status */}
                  <div className="p-4 md:p-6 border border-gray-200 rounded-lg">
                    <h3 className="font-semibold text-gray-800 mb-4">
                      Current Status
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-500 mb-1">
                          Status
                        </label>
                        <span
                          className={`px-2 md:px-3 py-1 rounded-full font-medium text-xs md:text-sm ${
                            driverData?.status === "active"
                              ? "bg-green-100 text-green-800"
                              : driverData?.status === "on_duty"
                              ? "bg-blue-100 text-blue-800"
                              : driverData?.status === "on_trip"
                              ? "bg-purple-100 text-purple-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {driverData?.status?.toUpperCase() || "INACTIVE"}
                        </span>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-500 mb-1">
                          Availability
                        </label>
                        <span
                          className={`px-2 md:px-3 py-1 rounded-full font-medium text-xs md:text-sm ${
                            driverData?.availability === "available"
                              ? "bg-green-100 text-green-800"
                              : driverData?.availability === "unavailable"
                              ? "bg-red-100 text-red-800"
                              : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {driverData?.availability?.toUpperCase() || "UNKNOWN"}
                        </span>
                      </div>
                    </div>
                    {driverData?.lastCheckIn && (
                      <div className="mt-4">
                        <label className="block text-sm text-gray-500 mb-1">
                          Last Check-in
                        </label>
                        <p className="font-medium text-gray-900">
                          {new Date(driverData.lastCheckIn).toLocaleString()}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-6">
                  {/* Profile Summary */}
                  <div className="p-4 md:p-6 bg-gradient-to-br from-[#304a3f]/10 to-[#e28743]/10 rounded-lg">
                    <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-4 rounded-full bg-gradient-to-r from-[#e28743] to-[#304a3f] flex items-center justify-center">
                      <User className="w-8 h-8 md:w-10 md:h-10 text-white" />
                    </div>
                    <h3 className="text-center font-semibold text-gray-900 mb-2">
                      {driverData?.name || "Driver"}
                    </h3>
                    <p className="text-center text-sm text-gray-600 mb-4">
                      Professional Driver
                    </p>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Driver ID</span>
                        <span className="font-medium text-sm">
                          {driverData?.driverId || "N/A"}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Rating</span>
                        <span className="font-medium text-sm flex items-center">
                          {ratingStats?.averageRating?.toFixed(1) ||
                            driverData?.rating?.toFixed(1) ||
                            "0.0"}
                          <Star className="w-4 h-4 text-yellow-400 ml-1" />
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">
                          Member Since
                        </span>
                        <span className="font-medium text-sm">
                          {driverData?.createdAt
                            ? new Date(driverData.createdAt).toLocaleDateString()
                            : "N/A"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="p-4 md:p-6 border border-gray-200 rounded-lg">
                    <h3 className="font-semibold text-gray-800 mb-4">
                      Profile Actions
                    </h3>
                    <div className="space-y-3">
                      <button
                        onClick={() => alert("Edit profile feature coming soon!")}
                        className="w-full py-3 bg-white border-2 border-[#304a3f] text-[#304a3f] font-medium rounded-lg hover:bg-[#304a3f]/5 transition-colors text-sm md:text-base"
                      >
                        Edit Profile
                      </button>
                      <button
                        onClick={() =>
                          alert("Change password feature coming soon!")
                        }
                        className="w-full py-3 bg-white border-2 border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors text-sm md:text-base"
                      >
                        Change Password
                      </button>
                      <button
                        onClick={() =>
                          alert("Document upload feature coming soon!")
                        }
                        className="w-full py-3 bg-white border-2 border-blue-500 text-blue-500 font-medium rounded-lg hover:bg-blue-50 transition-colors text-sm md:text-base"
                      >
                        Upload Documents
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-gray-200 text-center">
            <p className="text-xs md:text-sm text-gray-500">
              Driver ID: {driverData?.driverId || "N/A"} • License:{" "}
              {driverData?.licenseNumber || "N/A"} • Status:{" "}
              {driverData?.status || "Unknown"} • Last Update:{" "}
              {driverData?.lastCheckIn
                ? new Date(driverData.lastCheckIn).toLocaleString()
                : "Never"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DriverDashboard;